import { Injectable } from '@angular/core';
import { HttpLogado } from '../HttpLogado';
import { environment } from '../../environments/environment';
import { Task, interpretServerTask, convertToServerTask } from '../../classes/task';
import { LocalStorageService } from '../local-storage/local-storage.service';
import convertHttpErrorToLocal from 'src/utils/convertHttpErrorToLocal';

@Injectable()
export class TaskService {
  constructor(
    private http: HttpLogado,
    private localStorage: LocalStorageService
  ) { }

  /**
   * How long should the task data remain in session before reloading it from server
   */
  readonly persistMinutes: number = 1;

  /**
   * Retrieves the task list from the server
   */
  async fetchTasks(): Promise<any> {
    return await this.http.get(`${environment.baseUrl}/tasks`).toPromise();
  }

  /**
   * Retrieves or loads the task list
   */
  async getTaskList(forced?: boolean): Promise<{cached: boolean, list: Task[]}> {
    const cacheKey = `task-list`;

    if (forced) {
      this.localStorage.clearItem(cacheKey);
    } else {
      const cachedData = this.localStorage.getItem(cacheKey);
      if (cachedData && !forced) {
        const object = JSON.parse(cachedData);
        object.cached = true;
        return object;
      }
    }

    try {
      const list: any = await this.fetchTasks();
      const object = {cached: false, list: list.map(interpretServerTask)};
      const objectJSON = JSON.stringify(object);
      this.localStorage.setItem(cacheKey, objectJSON, this.persistMinutes / 60);
      return object;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  clearTaskListCache() {
    this.localStorage.clearItem(`task-list`);
  }

  /**
   * Retrieves the task information from the server
   * @param idTask The unique identifier of the task
   */
  async getTask(idTask: number): Promise<Task> {
    try {
      const response = await (this.http.get(`${environment.baseUrl}/tasks/${idTask}`).toPromise());

      return interpretServerTask(response);
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }
  /**
   * Clone the task
   * @param idTask The unique identifier of the task
   */
  async cloneTask(idTask: number) {
    try {
      const response = await (this.http.get(`${environment.baseUrl}/tasks/${idTask}/clone`).toPromise());
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  /**
   * Sends a delete request to the server for a specific task
   * @param idTask The unique identifier of the task
   */
  async removeTask(idTask: number) {
    this.clearTaskListCache();
    try {
      return await this.http.delete(`${environment.baseUrl}/tasks/${idTask}`).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  /**
   * Changes de state of a single task
   * @param idTask The unique identifier of the task
   * @param status The status to set the task to
   */
  async changeStatus(idTask: number, status: 'I' | 'A'): Promise<any> {
    this.clearTaskListCache();
    try {
      return await this.http.patch(`${environment.baseUrl}/tasks/${idTask}`, {status}).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async addTask(task: Task) {
    this.clearTaskListCache();
    try {
      return await this.http.post(`${environment.baseUrl}/tasks/v2`, convertToServerTask(task)).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async editTask(task: Task, idTask: number) {
    this.clearTaskListCache();
    try {
      return await this.http.put(`${environment.baseUrl}/tasks/v2/${idTask}`, convertToServerTask(task)).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }
}
